import { Badge, Group, Select, Switch, Text } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import React from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { TableMode } from 'src/common/components/table/hooks/useTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { BackgroundProcessDetailModal } from './BackgroundProcessDetailModal';
import {
  AdminBackgroundProcess,
  BACKGROUND_PROCESS_STATUSES,
  BackgroundProcessStatus,
} from './types';

type BackgroundProcessesTableProps = {
  company: Company;
};

function getStatusColor(status: BackgroundProcessStatus): string {
  switch (status) {
    case 'SUCCESS':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'PROCESSING':
      return 'blue';
    case 'PENDING':
    default:
      return 'gray';
  }
}

export function BackgroundProcessesTable({ company }: BackgroundProcessesTableProps) {
  const [selectedProcessId, setSelectedProcessId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<TableMode>('server');
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);

  const columns: DataTableColumn<AdminBackgroundProcess>[] = [
    {
      accessor: 'id',
      title: 'ID',
      width: 100,
      render: (record: AdminBackgroundProcess) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.id.split('-')[0]}
        </Text>
      ),
    },
    {
      accessor: 'processName',
      title: 'Process name',
      ellipsis: true,
      render: (record: AdminBackgroundProcess) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10" truncate="end">
          {record.processName}
        </Text>
      ),
    },
    {
      accessor: 'status',
      title: 'Status',
      width: 120,
      render: (record: AdminBackgroundProcess) => (
        <Badge color={getStatusColor(record.status)} variant="light">
          {record.status}
        </Badge>
      ),
    },
    {
      accessor: 'statusMessage',
      title: 'Status message',
      ellipsis: true,
      render: (record: AdminBackgroundProcess) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10" truncate="end">
          {record.statusMessage}
        </Text>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Created',
      width: 180,
      sortable: true,
      render: (record: AdminBackgroundProcess) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {new Date(record.createdAt).toLocaleString()}
        </Text>
      ),
    },
    {
      accessor: 'updatedAt',
      title: 'Updated',
      width: 180,
      sortable: true,
      render: (record: AdminBackgroundProcess) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {new Date(record.updatedAt).toLocaleString()}
        </Text>
      ),
    },
  ];

  return (
    <>
      <BackgroundProcessDetailModal
        processId={selectedProcessId}
        companyId={company.id}
        onClose={() => setSelectedProcessId(null)}
      />

      <SimpleTable<AdminBackgroundProcess>
        key={`${mode}-${statusFilter}`}
        mode={mode}
        resource={`admin/companies/${company.id}/background-processes`}
        filters={statusFilter ? { status: statusFilter } : undefined}
        title="Background processes"
        columns={columns}
        initialSort={{ columnAccessor: 'updatedAt', direction: 'desc' }}
        onRowClick={(record: AdminBackgroundProcess) => setSelectedProcessId(record.id)}
        showCount
        HeaderRow={
          <Group>
            <Select
              placeholder="Filter by status"
              data={BACKGROUND_PROCESS_STATUSES}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              size="sm"
              w={180}
            />
            <Switch
              label="Enable search (loads all data, so may be slow)"
              checked={mode === 'client'}
              onChange={e => setMode(e.currentTarget.checked ? 'client' : 'server')}
            />
          </Group>
        }
      />
    </>
  );
}
